import { useState } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  aspectRatio?: string;
  fallbackSrc?: string;
}

export const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className,
  loading = 'lazy',
  priority = false,
  aspectRatio = '16/9',
  fallbackSrc = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&h=450&fit=crop'
}: OptimizedImageProps) => {
  // Priority images skip the loading-state machinery entirely so the browser
  // can paint them immediately — critical for LCP.
  const [isLoaded, setIsLoaded] = useState(priority);
  const [hasError, setHasError] = useState(false);

  // Optimize Unsplash URLs
  const getOptimizedUrl = (url: string) => {
    if (!url) return fallbackSrc;
    if (url.includes('unsplash.com')) {
      const baseUrl = url.split('?')[0];
      const params = new URLSearchParams();
      params.set('w', (width || 800).toString());
      params.set('h', (height || 450).toString());
      params.set('fit', 'crop');
      params.set('auto', 'format,compress');
      params.set('fm', 'webp');
      params.set('q', '65');
      return `${baseUrl}?${params.toString()}`;
    }
    return url;
  };

  const imageSrc = hasError ? fallbackSrc : getOptimizedUrl(src);

  const handleError = () => {
    if (!hasError) {
      console.log('Image failed to load:', src, 'falling back to:', fallbackSrc);
      setHasError(true);
    }
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const containerStyle = width && height
    ? { aspectRatio: `${width}/${height}` }
    : { aspectRatio };

  return (
    <div
      className={cn("overflow-hidden bg-muted relative", className)}
      style={containerStyle}
    >
      {/* Placeholder only for non-priority images */}
      {!isLoaded && !priority && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      <img
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : loading}
        // @ts-expect-error fetchpriority is a valid HTML attribute
        fetchpriority={priority ? 'high' : undefined}
        decoding={priority ? 'sync' : 'async'}
        onError={handleError}
        onLoad={handleLoad}
        className={cn(
          "w-full h-full object-cover",
          priority
            ? "opacity-100"
            : cn("transition-opacity duration-300", isLoaded ? "opacity-100" : "opacity-0")
        )}
      />
    </div>
  );
};